import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MapaGL, type Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { EstadoUnidad } from '@tms/contratos';
import { MAP_STYLE_CLARO, MAP_STYLE_OSCURO } from '../config';
import { useTema, type Tema } from '../state/Tema';
import { UNIDAD } from './estados';

export interface MarcadorUnidad {
  id: string;
  lat: number;
  lon: number;
  rumbo: number | null;
  estado: EstadoUnidad;
  titulo: string;
  seleccionado: boolean;
}

/** Punto informativo: parada, detención, origen/destino. `variante` es la clase CSS del marcador. */
export interface PuntoMapa {
  id: string;
  lat: number;
  lon: number;
  variante: string;
  /** Texto corto dentro del marcador (p. ej. el número de parada). */
  texto: string;
  titulo: string;
  detalle: string;
  /** Se puede arrastrar (editor de rutas): al soltar se llama a `onArrastrarPunto`. */
  arrastrable?: boolean;
  /** Sin popup al hacer clic (el clic lo maneja `onClickPunto`). */
  sinPopup?: boolean;
  /** Resaltado (p. ej. la parada que se está editando). */
  seleccionado?: boolean;
}

/**
 * Estilo de una línea:
 * planificada (punteada) y real, del viaje; ruta, ruta-defecto, ruta-inactiva y ruta-seleccionada, de la troncal;
 * previa, la traza que se está editando.
 */
export type VarianteLinea = 'planificada' | 'real' | 'ruta' | 'ruta-defecto' | 'ruta-inactiva' | 'ruta-seleccionada' | 'previa';

export interface LineaMapa {
  id: string;
  variante: VarianteLinea;
  /** [longitud, latitud] */
  coords: [number, number][];
}

export interface Encuadre {
  clave: string | number;
  /** [longitud, latitud] */
  puntos: [number, number][];
  zoomMax?: number;
}

export interface VueloA {
  clave: string | number;
  lat: number;
  lon: number;
  zoom?: number;
}

interface Props {
  unidades?: MarcadorUnidad[];
  puntos?: PuntoMapa[];
  lineas?: LineaMapa[];
  encuadre?: Encuadre;
  vueloA?: VueloA;
  paddingEncuadre?: { top: number; bottom: number; left: number; right: number };
  onSeleccionarUnidad?: (id: string) => void;
  /** Clic en el mapa (fuera de los marcadores). Si está, el cursor es una cruz. */
  onClickMapa?: (lat: number, lon: number) => void;
  onClickPunto?: (id: string) => void;
  onArrastrarPunto?: (id: string, lat: number, lon: number) => void;
  className?: string;
}

const MENSAJE_ERROR = 'No se pudo cargar el mapa. Revisá tu conexión y recargá la página. La lista de unidades sigue disponible.';
const estiloDe = (t: Tema) => (t === 'dark' ? MAP_STYLE_OSCURO : MAP_STYLE_CLARO);
const VACIO: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

const SVG_UNIDAD: Record<EstadoUnidad, (rumbo: number) => string> = {
  EN_RUTA: (r) => `<svg viewBox="0 0 24 24" aria-hidden="true" style="transform:rotate(${r}deg)"><path d="M12 2 21 22 12 17 3 22Z"/></svg>`,
  DETENIDA: () => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6v12M15 6v12"/></svg>',
  SIN_REPORTE: () => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
  SIN_ACTIVIDAD: () => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12"/></svg>',
};

/** Orden de dibujo: de abajo hacia arriba. */
const VARIANTES: VarianteLinea[] = ['ruta-inactiva', 'ruta', 'ruta-defecto', 'planificada', 'previa', 'ruta-seleccionada', 'real'];

function pinturaLinea(v: VarianteLinea, tema: Tema): maplibregl.LineLayerSpecification['paint'] {
  const oscuro = tema === 'dark';
  const gris = oscuro ? '#98a6ae' : '#617681';
  const verde = oscuro ? '#57bea5' : '#18745f';
  switch (v) {
    case 'planificada':
      return { 'line-color': gris, 'line-width': 3, 'line-dasharray': [2, 2.5], 'line-opacity': 0.9 };
    case 'real':
      return { 'line-color': '#49b79e', 'line-width': 4 };
    case 'ruta':
      return { 'line-color': gris, 'line-width': 3, 'line-opacity': 0.75 };
    case 'ruta-defecto':
      return { 'line-color': verde, 'line-width': 4 };
    case 'ruta-inactiva':
      return { 'line-color': gris, 'line-width': 2, 'line-dasharray': [1, 2], 'line-opacity': 0.5 };
    case 'ruta-seleccionada':
      return { 'line-color': oscuro ? '#e7edef' : '#18262d', 'line-width': 5 };
    case 'previa':
      return { 'line-color': verde, 'line-width': 5 };
  }
}

function agregarCapas(mapa: MapaGL, tema: Tema): void {
  for (const v of VARIANTES) {
    const id = `linea-${v}`;
    if (!mapa.getSource(id)) mapa.addSource(id, { type: 'geojson', data: VACIO });
    // Borde oscuro debajo de la ruta seleccionada y de la previa, para que se lean sobre las demás.
    if ((v === 'ruta-seleccionada' || v === 'previa') && !mapa.getLayer(`${id}-borde`)) {
      mapa.addLayer({
        id: `${id}-borde`,
        type: 'line',
        source: id,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': tema === 'dark' ? '#0c1117' : '#ffffff', 'line-width': 9, 'line-opacity': 0.8 },
      });
    }
    if (!mapa.getLayer(id)) mapa.addLayer({ id, type: 'line', source: id, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: pinturaLinea(v, tema) });
  }
}

function aplicarLineas(mapa: MapaGL, lineas: LineaMapa[]): void {
  for (const v of VARIANTES) {
    const fuente = mapa.getSource(`linea-${v}`) as maplibregl.GeoJSONSource | undefined;
    if (!fuente) continue;
    const features: GeoJSON.Feature[] = lineas
      .filter((l) => l.variante === v && l.coords.length >= 2)
      .map((l) => ({ type: 'Feature', properties: { id: l.id }, geometry: { type: 'LineString', coordinates: l.coords } }));
    fuente.setData({ type: 'FeatureCollection', features });
  }
}

interface MarcaUnidad {
  marker: Marker;
  span: HTMLElement;
  clave: string;
}
interface MarcaPunto {
  marker: Marker;
  clave: string;
}

/**
 * Wrapper de MapLibre GL. Estilo por tema (VITE_MAP_STYLE_URL / VITE_MAP_STYLE_URL_CLARO).
 * Al desmontar elimina marcadores, capas y listeners (map.remove()).
 */
export function Mapa({
  unidades = [],
  puntos = [],
  lineas = [],
  encuadre,
  vueloA,
  paddingEncuadre,
  onSeleccionarUnidad,
  onClickMapa,
  onClickPunto,
  onArrastrarPunto,
  className = '',
}: Props) {
  const { tema } = useTema();
  const contenedor = useRef<HTMLDivElement>(null);
  const mapaRef = useRef<MapaGL | null>(null);
  const listo = useRef(false);
  const cola = useRef<(() => void)[]>([]);
  const marcasU = useRef(new Map<string, MarcaUnidad>());
  const marcasP = useRef(new Map<string, MarcaPunto>());
  const [error, setError] = useState<string | null>(null);

  // Últimos valores para usarlos desde callbacks del mapa sin recrearlo.
  const lineasRef = useRef(lineas);
  lineasRef.current = lineas;
  const temaRef = useRef(tema);
  temaRef.current = tema;
  const alSeleccionar = useRef(onSeleccionarUnidad);
  alSeleccionar.current = onSeleccionarUnidad;
  const alClickMapa = useRef(onClickMapa);
  alClickMapa.current = onClickMapa;
  const alClickPunto = useRef(onClickPunto);
  alClickPunto.current = onClickPunto;
  const alArrastrar = useRef(onArrastrarPunto);
  alArrastrar.current = onArrastrarPunto;

  const cuandoListo = (fn: () => void) => {
    if (listo.current) fn();
    else cola.current.push(fn);
  };

  // Ciclo de vida del mapa.
  useEffect(() => {
    const el = contenedor.current;
    if (!el) return;
    let mapa: MapaGL;
    try {
      mapa = new maplibregl.Map({
        container: el,
        style: estiloDe(temaRef.current),
        center: [-66.8, -44.2],
        zoom: 5.4,
        attributionControl: { compact: true },
      });
    } catch (e) {
      console.error('No se pudo crear el mapa', e);
      setError(MENSAJE_ERROR);
      return;
    }
    mapaRef.current = mapa;
    setError(null);
    mapa.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    const alCargarEstilo = () => {
      listo.current = true;
      agregarCapas(mapa, temaRef.current);
      aplicarLineas(mapa, lineasRef.current);
      const pendientes = cola.current.splice(0);
      for (const fn of pendientes) fn();
    };
    const alError = (e: { error?: Error }) => {
      // Solo se avisa si el estilo base nunca cargó; los errores de tiles sueltos no rompen la pantalla.
      if (!listo.current) {
        console.error('Error del mapa', e.error);
        setError(MENSAJE_ERROR);
      }
    };
    // Los clics sobre marcadores también llegan al mapa (así abre su popup): se ignoran acá.
    const alClick = (e: maplibregl.MapMouseEvent) => {
      const destino = e.originalEvent.target;
      if (destino instanceof Element && destino.closest('.maplibregl-marker')) return;
      alClickMapa.current?.(e.lngLat.lat, e.lngLat.lng);
    };
    mapa.on('style.load', alCargarEstilo);
    mapa.on('error', alError);
    mapa.on('click', alClick);

    const observador = new ResizeObserver(() => mapa.resize());
    observador.observe(el);

    const marcasUnidad = marcasU.current;
    const marcasPunto = marcasP.current;
    return () => {
      observador.disconnect();
      mapa.off('style.load', alCargarEstilo);
      mapa.off('error', alError);
      mapa.off('click', alClick);
      for (const m of marcasUnidad.values()) m.marker.remove();
      for (const m of marcasPunto.values()) m.marker.remove();
      marcasUnidad.clear();
      marcasPunto.clear();
      cola.current = [];
      listo.current = false;
      mapa.remove();
      mapaRef.current = null;
    };
  }, []);

  // Cambio de tema: otro estilo base; las capas se reagregan en 'style.load'.
  const temaAplicado = useRef<Tema>(tema);
  useEffect(() => {
    if (temaAplicado.current === tema) return;
    temaAplicado.current = tema;
    // diff:false fuerza la recarga completa: así se dispara style.load y se reagregan las capas propias.
    mapaRef.current?.setStyle(estiloDe(tema), { diff: false });
  }, [tema]);

  // Cursor en cruz cuando un clic en el mapa agrega algo (editor de rutas).
  const conClick = onClickMapa !== undefined;
  useEffect(() => {
    const lienzo = mapaRef.current?.getCanvas();
    if (lienzo) lienzo.style.cursor = conClick ? 'crosshair' : '';
  }, [conClick]);

  // Líneas.
  useEffect(() => {
    const mapa = mapaRef.current;
    if (mapa && listo.current) aplicarLineas(mapa, lineas);
  }, [lineas]);

  // Marcadores de unidades (flechas rotadas por rumbo, color e ícono por estado).
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;
    const vistos = new Set<string>();
    for (const u of unidades) {
      vistos.add(u.id);
      const rumbo = Math.round(u.rumbo ?? 0);
      const clave = `${u.estado}|${rumbo}|${u.seleccionado}`;
      let m = marcasU.current.get(u.id);
      if (!m) {
        const boton = document.createElement('button');
        boton.type = 'button';
        boton.className = 'marker';
        const span = document.createElement('span');
        boton.appendChild(span);
        const id = u.id;
        boton.addEventListener('click', (ev) => {
          ev.stopPropagation();
          alSeleccionar.current?.(id);
        });
        const marker = new maplibregl.Marker({ element: boton }).setLngLat([u.lon, u.lat]).addTo(mapa);
        m = { marker, span, clave: '' };
        marcasU.current.set(u.id, m);
      }
      m.marker.setLngLat([u.lon, u.lat]);
      const boton = m.marker.getElement();
      boton.title = u.titulo;
      boton.setAttribute('aria-label', u.titulo);
      boton.style.zIndex = u.seleccionado ? '10' : '1';
      if (m.clave !== clave) {
        const color = UNIDAD[u.estado].color;
        m.span.className = `marker-arrow ${color} ${u.seleccionado ? 'chosen' : ''} ${u.estado === 'EN_RUTA' ? '' : 'glyph'}`;
        m.span.innerHTML = SVG_UNIDAD[u.estado](rumbo);
        m.clave = clave;
      }
    }
    for (const [id, m] of marcasU.current) {
      if (!vistos.has(id)) {
        m.marker.remove();
        marcasU.current.delete(id);
      }
    }
  }, [unidades]);

  // Marcadores de puntos (paradas, detenciones).
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;
    const vistos = new Set<string>();
    for (const p of puntos) {
      vistos.add(p.id);
      const clave = `${p.variante}|${p.texto}|${p.titulo}|${p.detalle}|${p.lat}|${p.lon}|${p.arrastrable ?? false}|${p.seleccionado ?? false}|${p.sinPopup ?? false}`;
      const previo = marcasP.current.get(p.id);
      if (previo?.clave === clave) continue;
      previo?.marker.remove();
      const el = document.createElement('div');
      el.className = `punto ${p.variante}${p.seleccionado ? ' sel' : ''}${p.arrastrable ? ' arrastrable' : ''}`;
      el.title = p.titulo;
      el.textContent = p.texto;
      const id = p.id;
      el.addEventListener('click', () => alClickPunto.current?.(id));
      const marker = new maplibregl.Marker({ element: el, draggable: p.arrastrable ?? false }).setLngLat([p.lon, p.lat]);
      if (!p.sinPopup) {
        const contenido = document.createElement('div');
        const t = document.createElement('strong');
        t.textContent = p.titulo;
        const d = document.createElement('div');
        d.textContent = p.detalle;
        contenido.append(t, d);
        marker.setPopup(new maplibregl.Popup({ offset: 14 }).setDOMContent(contenido));
      }
      if (p.arrastrable) {
        marker.on('dragend', () => {
          const { lat, lng } = marker.getLngLat();
          alArrastrar.current?.(id, lat, lng);
        });
      }
      marker.addTo(mapa);
      marcasP.current.set(p.id, { marker, clave });
    }
    for (const [id, m] of marcasP.current) {
      if (!vistos.has(id)) {
        m.marker.remove();
        marcasP.current.delete(id);
      }
    }
  }, [puntos]);

  // Encuadre: se ejecuta cuando cambia la clave.
  const claveEncuadre = encuadre?.clave;
  useEffect(() => {
    if (!encuadre || encuadre.puntos.length === 0) return;
    const { puntos: pts, zoomMax } = encuadre;
    cuandoListo(() => {
      const mapa = mapaRef.current;
      if (!mapa) return;
      if (pts.length === 1) {
        mapa.flyTo({ center: pts[0], zoom: zoomMax ?? 9 });
        return;
      }
      const b = new maplibregl.LngLatBounds(pts[0], pts[0]);
      for (const p of pts) b.extend(p);
      mapa.fitBounds(b, { padding: paddingEncuadre ?? 50, maxZoom: zoomMax ?? 12, duration: 600 });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveEncuadre]);

  // Vuelo a un punto (Ver en mapa / selección desde la lista).
  const claveVuelo = vueloA?.clave;
  useEffect(() => {
    if (!vueloA) return;
    const { lat, lon, zoom } = vueloA;
    cuandoListo(() => {
      const mapa = mapaRef.current;
      mapa?.flyTo({ center: [lon, lat], zoom: Math.max(mapa.getZoom(), zoom ?? 8), duration: 700 });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveVuelo]);

  return (
    <>
      <div ref={contenedor} className={`mapa ${className}`} role="region" aria-label="Mapa" />
      {error && (
        <p className="mapa-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
